import { Component } from '@theme/component';
import { CartLinesUpdateEvent } from '@shopify/events';

/**
 * CHAMFER — the tiered bundle cards.
 *
 * PROGRESSIVE ENHANCEMENT
 * Each card's call to action is a real link to `/cart/add?id=<variant>`, so a
 * click adds the bundle even if this file never runs — a blocked module, an
 * import map that resolved late, a custom element not yet upgraded inside a
 * section morph. That path costs a page load and skips the reconcile below,
 * but it always works. This file intercepts the click and does the same thing
 * without leaving the cart, plus the tidying only JavaScript can do.
 *
 * NO DUPLICATES
 * After the tier is added the cart is re-read and two kinds of line are taken
 * back out: a loose component line the new set already contains (one unit, so a
 * quantity above one keeps the rest), and any other tier whose contents are a
 * subset of the new one. Nothing else is touched, and a tier already in the
 * cart renders as static text rather than a link, so the same set cannot be
 * added twice.
 *
 * The bundle is added BEFORE anything is removed, so a failure can only leave
 * the customer with more than they expected, never less. Removals resolve
 * against a freshly fetched cart by variant id, so a line key that moved
 * between render and click cannot touch the wrong line.
 */

/** One cart mutation at a time, across every card on the page. */
let inFlight = false;

/** @returns {'mobile' | 'tablet' | 'desktop'} */
function deviceType() {
  const w = window.innerWidth;
  if (w < 750) return 'mobile';
  if (w < 990) return 'tablet';
  return 'desktop';
}

/**
 * @param {string} name
 * @param {Record<string, unknown>} payload
 */
function cfTrack(name, payload = {}) {
  try {
    if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event: name, ...payload });
    document.dispatchEvent(new CustomEvent(`cf:${name}`, { detail: payload, bubbles: true }));
  } catch (error) {
    console.warn('[cf-bundle-tiers] analytics push failed', error);
  }
}

/** @returns {string} Comma separated ids of every cart section on the page. */
function cartSectionIds() {
  const ids = new Set();
  for (const node of document.querySelectorAll('cart-items-component')) {
    if (node instanceof HTMLElement && node.dataset.sectionId) ids.add(node.dataset.sectionId);
  }
  return Array.from(ids).join(',');
}

/** @returns {Promise<any>} */
async function fetchCart() {
  const response = await fetch(`${Theme.routes.cart_url}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`Cart could not be read: ${response.status}`);
  return response.json();
}

/** @param {string} message */
function announce(message) {
  const region = document.getElementById('cf-cart-live-region');
  if (!region) return;
  region.textContent = '';
  window.setTimeout(() => {
    region.textContent = message;
  }, 60);
}

class CfBundleTiers extends Component {
  /** @type {IntersectionObserver | null} */
  #observer = null;

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('click', this.#onClick);
    this.#watchForView();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('click', this.#onClick);
    this.#observer?.disconnect();
    this.#observer = null;
  }

  /** @returns {string} */
  get currency() {
    return this.dataset.currency || 'USD';
  }

  /** Every tier card on this element, as parsed payloads. */
  get cards() {
    return [...this.querySelectorAll('[data-cf-tier]')].flatMap((card) => {
      const script = card.querySelector('[data-cf-tier-data]');
      if (!script?.textContent) return [];
      try {
        return [{ card, data: JSON.parse(script.textContent) }];
      } catch (error) {
        console.warn('[cf-bundle-tiers] could not parse a tier payload', error);
        return [];
      }
    });
  }

  /**
   * @param {Element} card
   * @returns {any}
   */
  #dataFor(card) {
    const found = this.cards.find((entry) => entry.card === card);
    return found ? found.data : null;
  }

  #watchForView() {
    if (typeof IntersectionObserver === 'undefined') {
      cfTrack('bundle_offer_view', this.#baseAnalytics());
      return;
    }
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          this.#observer?.disconnect();
          this.#observer = null;
          cfTrack('bundle_offer_view', this.#baseAnalytics());
        }
      },
      { threshold: 0.3 }
    );
    this.#observer.observe(this);
  }

  /** @returns {Record<string, unknown>} */
  #baseAnalytics(data, variant) {
    return {
      context: this.dataset.context || 'drawer',
      device_type: deviceType(),
      currency: this.currency,
      cart_value_before: Number(this.dataset.cartTotal || 0) / 100,
      tiers_shown: this.cards.map((entry) => entry.data.tier),
      ...(data
        ? {
            tier: data.tier,
            bundle_name: data.name,
            bundle_id: data.handle,
            bundle_variant_id: variant ? String(variant.id) : undefined,
            value: variant ? variant.price / 100 : undefined,
          }
        : {}),
    };
  }

  /**
   * @param {Element} card
   * @param {string} message
   */
  #hint(card, message) {
    const hint = card.querySelector('[data-cf-tier-hint]');
    if (hint) hint.textContent = message;
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const trigger = target.closest('[data-cf-tier-add]');
    if (!(trigger instanceof HTMLElement)) return;
    if (trigger.getAttribute('aria-disabled') === 'true') {
      event.preventDefault();
      return;
    }

    // Let modified clicks (new tab, etc.) behave like an ordinary link.
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button !== 0) {
      return;
    }

    const card = trigger.closest('[data-cf-tier]');
    if (!card) return;

    const data = this.#dataFor(card);
    const variant = data?.variants?.[0];

    // No usable payload: leave the link alone so the browser adds it the plain
    // way rather than swallowing the click.
    if (!variant) return;

    event.preventDefault();
    this.#run(card, trigger, data, variant);
  };

  /**
   * @param {Element} card
   * @param {HTMLElement} trigger
   * @param {any} data
   * @param {any} variant
   */
  async #run(card, trigger, data, variant) {
    if (inFlight || trigger.hasAttribute('data-busy')) return;

    cfTrack('bundle_upgrade_click', this.#baseAnalytics(data, variant));
    this.#hint(card, '');
    this.#busy(trigger, true);

    try {
      await this.#add(data, variant);
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'That could not be added. Please try again.';
      this.#hint(card, message);
      announce(message);
      cfTrack('bundle_upgrade_error', { ...this.#baseAnalytics(data, variant), message });
    } finally {
      this.#busy(trigger, false);
    }
  }

  /**
   * @param {HTMLElement} trigger
   * @param {boolean} state
   */
  #busy(trigger, state) {
    inFlight = state;
    if (state) {
      trigger.setAttribute('data-busy', 'true');
      trigger.setAttribute('aria-busy', 'true');
      announce('Updating your order.');
    } else {
      trigger.removeAttribute('data-busy');
      trigger.removeAttribute('aria-busy');
    }
  }

  /**
   * @param {any} data
   * @param {any} variant
   */
  async #add(data, variant) {
    const sections = cartSectionIds();
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: String(variant.id), quantity: 1 }],
        promise: deferred.promise,
      })
    );

    try {
      const addResponse = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ items: [{ id: variant.id, quantity: 1 }] }),
      });
      const added = await addResponse.json();
      if (added.status || added.description) {
        throw new Error(added.description || added.message || 'That set could not be added right now.');
      }

      const current = await fetchCart();
      const contents = new Set(variant.components.map(Number));

      const tierVariants = new Map();
      for (const entry of this.cards) {
        for (const v of entry.data.variants) tierVariants.set(Number(v.id), v);
      }

      /** @type {Record<string, number>} */
      const updates = {};
      for (const line of current.items) {
        const id = Number(line.variant_id);
        if (id === Number(variant.id)) continue;

        const asTier = tierVariants.get(id);
        if (asTier) {
          const subsumed =
            asTier.components.length > 0 &&
            asTier.components.every((c) => contents.has(Number(c)));
          if (subsumed) updates[line.key] = 0;
          continue;
        }

        if (contents.has(id)) updates[line.key] = Math.max(0, line.quantity - 1);
      }

      let cart = current;
      let sectionsPayload;

      if (Object.keys(updates).length > 0) {
        const updateResponse = await fetch(`${Theme.routes.cart_url}/update.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ updates, sections, sections_url: window.location.pathname }),
        });
        const updated = await updateResponse.json();
        if (updated.status || updated.description) {
          throw new Error(
            updated.description ||
              updated.message ||
              'The set was added but the items it replaces could not be removed. Please review your cart.'
          );
        }
        sectionsPayload = updated.sections;
        cart = await fetchCart();
      } else if (sections) {
        const rendered = await fetch(
          `${window.location.pathname}?sections=${encodeURIComponent(sections)}`,
          { credentials: 'same-origin' }
        );
        sectionsPayload = await rendered.json();
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          itemCount: cart.item_count,
          sections: sectionsPayload,
          source: 'cf-bundle-tiers',
          didError: false,
        },
      });

      announce(`${data.name} added to your order.`);

      cfTrack('bundle_upgrade_success', {
        ...this.#baseAnalytics(data, variant),
        cart_value_after: cart.total_price / 100,
        replaced_lines: Object.keys(updates).length,
        items: [
          {
            item_id: String(data.productId),
            variant_id: String(variant.id),
            item_name: data.name,
            price: variant.price / 100,
            quantity: 1,
          },
        ],
      });
    } catch (error) {
      deferred.reject(error instanceof Error ? error : new Error(String(error)));
      throw error;
    }
  }
}

if (!customElements.get('cf-bundle-tiers')) {
  customElements.define('cf-bundle-tiers', CfBundleTiers);
}
